import styled from 'styled-components';
import React, { useContext, useEffect } from 'react';
import {
  Label,
  LabelSelector,
  ListWrapper,
  Title,
  TitleHint,
} from './components';
import { EditorContext } from '../../../utils/contexts';
import Switcher from '../components/Switcher';
import { useSnackbar } from 'notistack';
import SelectInput from '../components/SelectInput';
import {
  Tooltip,
  TextField,
  Autocomplete,
  Chip,
  FormControl,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { withEditorPageTracking } from '../../../shared/Tracking';
import '../../../assets/styles/_settings.scss';
import NumberInput from '../components/NumberInput';
import InputBlock from '../components/InputBlock';
import useFetch from '../../../utils/useFetch';

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    maxWidth: 300,
    fontSize: theme.typography.pxToRem(16),
  },
}))(Tooltip);

const AutoCompleteBlock = styled(FormControl)`
  .css-16awh2u-MuiAutocomplete-root .MuiAutocomplete-tag {
    margin: 2px;
  }
  .css-6od3lo-MuiChip-label {
    padding-left: 6px;
    padding-right: 6px;
  }
  .css-2bh5uq-MuiButtonBase-root-MuiChip-root .MuiChip-deleteIcon {
    font-size: 15px;
    margin: 0 0 0 -6px;
  }
  .MuiAutocomplete-popper.css-1s5lphu-MuiPopper-root-MuiAutocomplete-popper-root {
    box-shadow: 2px 2px 2px 2px #404040;
  }
`;

function SettingsStep({ className, site }) {
  const { settings, updateSettings } = useContext(EditorContext);
  const { enqueueSnackbar } = useSnackbar();
  const abTestingEnabled = site?.capabilities?.ab_testing_enabled !== false;

  function toggleBranding() {
    updateSettings({ show_branding: !settings.show_branding });
  }

  function toggleAnimated() {
    updateSettings({ animated: !settings.animated });
  }

  function toggleABTest() {
    if (!abTestingEnabled) {
      enqueueSnackbar(
        'A/B testing is not available on your plan. Please upgrade your subscription.',
        { variant: 'error' }
      );
      return;
    }

    const turningOff = settings.is_ab_test === true;
    updateSettings({ is_ab_test: !settings.is_ab_test });
    if (turningOff) {
      try {
        window.analytics?.track('AB Test Disabled', {
          goal: settings.element_subtype,
          popup_type: settings.type,
          site_id: site?.id,
        });
      } catch (_) {}
    }
  }

  function toggleWiggle() {
    updateSettings({ wiggle_button: !settings.wiggle_button });
  }

  function toggleCloseable() {
    updateSettings({ closable: !settings.closable });
  }

  function togglePush() {
    updateSettings({ pushes_page_down: !settings.pushes_page_down });
  }

  function toggleRemains() {
    updateSettings({ remains_at_top: !settings.remains_at_top });
  }

  function toggleGdpr() {
    if (
      settings.enable_gdpr ||
      (site?.gdpr?.configured && !settings.enable_gdpr)
    ) {
      updateSettings({ enable_gdpr: !settings.enable_gdpr });
    } else {
      enqueueSnackbar(
        'Privacy settings have not been configured. Please navigate to privacy settings to enable GDPR/Double opt-in.',
        { variant: 'error' }
      );
    }
  }

  function toggleClosePopup() {
    updateSettings({ close_outside_popup: !settings.close_outside_popup });
  }

  const pushesPageText =
    !settings.placement || settings.placement === 'bar-top'
      ? 'Pushes page down'
      : 'Pushes page up';

  const isEmail = settings.subtype === 'email';
  const isBar = settings.type === 'Bar';
  const isModal = settings.type === 'Modal';
  const isAlert = settings.type === 'Alert';
  const isInline = settings.type === 'Inline';

  const soundOptions = [
    { value: 'bell', label: 'Bell' },
    { value: 'none', label: 'No sound' },
  ];

  function updateSound(value) {
    updateSettings({ sound: value });
  }

  function updateNotificationDelay(value) {
    updateSettings({ notification_delay: value });
  }

  const tooltips = {
    abTest: abTestingEnabled
      ? 'Enabling this would group this popup with other AB Test enabled popups in the same rule with the same Goal'
      : 'A/B testing is not available on your plan. Please upgrade your subscription.',
    branding:
      'Removing branding helps increase conversions on average by 6%. Upgrade to one of our paid plans to remove the H logo from your site elements.',
    animate:
      'If enabled, your bar will display just after the page loads and drop down from the top (or pop up from the bottom) of the page. This draws your site visitors attention to the bar. If enabled, this type of animation will also occur if a user closes your bar.',
  };

  useEffect(() => {
    window.hellobar?.displayPage && window.hellobar?.displayPage('default');
  }, []);

  const {
    status,
    data: { tags },
  } = useFetch('popupTags');

  const handleTagsChange = (value) => {
    updateSettings({ ...settings, tags: value });
  };

  return (
    <div className={className}>
      <div>
        <Title>Settings</Title>
        <TitleHint>Change how your Hello Bar will be shown.</TitleHint>
      </div>
      <ListWrapper>
        <Label>
          <HtmlTooltip placement="bottom" title={tooltips.branding}>
            <span>Hello Bar branding</span>
          </HtmlTooltip>
          <Switcher
            checked={settings.show_branding}
            onChange={toggleBranding}
          />
        </Label>
        <Label>
          <HtmlTooltip placement="bottom" title={tooltips.abTest}>
            <span>A/B Test Enabled</span>
          </HtmlTooltip>
          <Switcher checked={settings.is_ab_test} onChange={toggleABTest} />
        </Label>
        {!isInline && (
          <Label>
            <HtmlTooltip placement="bottom" title={tooltips.animate}>
              <span>Animate entry/exit</span>
            </HtmlTooltip>
            <Switcher checked={settings.animated} onChange={toggleAnimated} />
          </Label>
        )}
        <Label>
          <span>Wiggle button</span>
          <Switcher checked={settings.wiggle_button} onChange={toggleWiggle} />
        </Label>
        {!isAlert && !isInline && (
          <Label>
            <span>Allow visitors to hide {settings.type}</span>
            <Switcher checked={settings.closable} onChange={toggleCloseable} />
          </Label>
        )}
        {isBar && (!settings.placement || settings.placement === 'bar-top') && (
          <Label>
            <span>{pushesPageText}</span>
            <Switcher
              checked={settings.pushes_page_down}
              onChange={togglePush}
            />
          </Label>
        )}
        {isBar && (
          <Label>
            <span>Remains in place on scroll</span>
            <Switcher
              checked={settings.remains_at_top}
              onChange={toggleRemains}
            />
          </Label>
        )}
        {isEmail && (
          <Label>
            <span>GDPR Compliant/Double Opt-in</span>
            <Switcher checked={settings.enable_gdpr} onChange={toggleGdpr} />
          </Label>
        )}
        {isModal && (
          <Label>
            <span>Close By Clicking Outside Pop-Up</span>
            <Switcher
              checked={settings.close_outside_popup}
              onChange={toggleClosePopup}
            />
          </Label>
        )}
        <LabelSelector>
          <span>Tags</span>
          <AutoCompleteBlock>
            <Autocomplete
              id="tags-standard"
              multiple
              freeSolo
              options={status === 'fetched' ? tags : []}
              getOptionLabel={(option) => option.title || option}
              value={settings.tags}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    variant="outlined"
                    label={option}
                    sx={{
                      fontSize: '10px',
                      height: '20px',
                      backgroundColor: 'lightgray',
                      color: '#666666',
                    }}
                  />
                ))
              }
              onChange={(event, newValue) => {
                if (newValue.length < 6) {
                  handleTagsChange(newValue);
                } else {
                  enqueueSnackbar('You can add only 5 tags.', {
                    variant: 'error',
                  });
                }
              }}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    inputProps={{ ...params.inputProps, maxLength: 16 }}
                    variant="outlined"
                    label="Tags"
                    margin="normal"
                    fullWidth
                  />
                );
              }}
            />
          </AutoCompleteBlock>
        </LabelSelector>

        {isAlert && (
          <>
            <LabelSelector>
              <span>Notification</span>
              <SelectInput
                items={soundOptions}
                value={settings.sound}
                onChange={updateSound}
              />
            </LabelSelector>
            <InputBlock label="Notification delay">
              <NumberInput
                value={settings.notification_delay}
                onChange={updateNotificationDelay}
                max={600}
                type="s"
              />
            </InputBlock>
          </>
        )}
      </ListWrapper>
    </div>
  );
}

export default styled(withEditorPageTracking(SettingsStep, 'Settings'))``;
